import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import SEOHead from '../../components/SEOHead';

export default function LandingPage() {
  const navigate = useNavigate();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const ctaSectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const section = ctaSectionRef.current;
    if (!section) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !videoLoaded) {
            setVideoLoaded(true);
            observer.disconnect();
          }
        });
      },
      { rootMargin: '200px' }
    );

    observer.observe(section);
    return () => observer.disconnect();
  }, [videoLoaded]);

  const handleNewsletterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email) return;
    setIsSubmitting(true);
    try {
      const formData = new URLSearchParams();
      formData.append('email', email);
      await fetch('https://readdy.ai/api/form/d6pojjsddmmni7ck8650', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
      setSubmitted(true);
      setEmail('');
    } catch {
      setSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const softwareSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "MEUCONTROLE",
    "applicationCategory": "FinanceApplication",
    "operatingSystem": "Web",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "BRL"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "ratingCount": "1250"
    },
    "description": "Aplicativo completo de controle financeiro pessoal com IA. Gerencie despesas, receitas, orçamentos e metas financeiras de forma inteligente.",
    "url": siteUrl,
    "screenshot": `${siteUrl}/screenshot.png`,
    "author": {
      "@type": "Organization",
      "name": "MEUCONTROLE"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Como o MEUCONTROLE me ajuda a controlar minhas finanças?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "O MEUCONTROLE oferece ferramentas completas para gerenciar suas despesas, receitas, orçamentos e metas financeiras. Com nossa IA integrada, você recebe insights personalizados e lembretes automáticos para manter suas finanças organizadas."
        }
      },
      {
        "@type": "Question",
        "name": "O MEUCONTROLE é gratuito?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Sim! Oferecemos um plano gratuito com recursos essenciais. Para funcionalidades avançadas como assistente IA ilimitado e análises detalhadas, temos planos Pro e Premium com teste grátis de 7 dias."
        }
      },
      {
        "@type": "Question",
        "name": "Meus dados financeiros estão seguros?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Absolutamente! Utilizamos criptografia de ponta a ponta e armazenamento seguro em nuvem. Seus dados são protegidos com os mais altos padrões de segurança da indústria."
        }
      },
      {
        "@type": "Question",
        "name": "Posso cancelar minha assinatura a qualquer momento?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Sim, você pode cancelar sua assinatura a qualquer momento sem taxas de cancelamento. Você continuará tendo acesso aos recursos premium até o final do período pago."
        }
      }
    ]
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "MEUCONTROLE",
    "url": siteUrl,
    "logo": `${siteUrl}/logo.png`,
    "description": "Plataforma completa de controle financeiro pessoal com inteligência artificial",
    "sameAs": [
      "https://facebook.com/meucontrole",
      "https://twitter.com/meucontrole",
      "https://instagram.com/meucontrole"
    ]
  };

  return (
    <>
      <SEOHead
        title="MEUCONTROLE-Controle Financeiro Inteligente com IA"
        description="Aplicativo completo de controle financeiro pessoal com IA. Gerencie despesas, receitas, orçamentos e metas financeiras de forma inteligente. Teste grátis por 7 dias!"
        keywords="controle financeiro, finanças pessoais, orçamento pessoal, gestão financeira, app financeiro, controle de gastos, planejamento financeiro, IA financeira"
        canonical={siteUrl}
        schema={[softwareSchema, faqSchema, organizationSchema]}
      />

      <div className="min-h-screen bg-white">
        {/* Navbar */}
        <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white/80 backdrop-blur-md border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16 sm:h-20">
              <h1 
                className="text-2xl sm:text-3xl font-bold text-gray-900 cursor-pointer"
                style={{ fontFamily: '"Pacifico", serif' }}
              >
                MEUCONTROLE
              </h1>
              <div className="flex items-center gap-3 sm:gap-4">
                <button
                  onClick={() => navigate('/login')}
                  className="text-gray-600 hover:text-gray-900 font-medium text-sm cursor-pointer whitespace-nowrap hidden sm:block"
                >
                  Entrar
                </button>
                <button
                  onClick={() => navigate('/login')}
                  className="px-4 sm:px-6 py-2.5 bg-teal-600 text-white rounded-full font-semibold hover:bg-teal-700 transition-all cursor-pointer whitespace-nowrap text-sm shadow-lg shadow-teal-600/25"
                >
                  Começar Grátis
                </button>
              </div>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="pt-24 sm:pt-32 pb-16 sm:pb-24 px-4 sm:px-6 lg:px-8 overflow-hidden">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              {/* Left Content */}
              <div className="text-center lg:text-left">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 rounded-full mb-6 sm:mb-8">
                  <i className="ri-check-line text-emerald-600"></i>
                  <span className="text-emerald-700 text-sm font-medium">Grátis por 7 dias • Sem cartão</span>
                </div>
                
                <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
                  Pare de se sentir{' '}
                  <span className="text-teal-600 relative">
                    perdido
                    <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 200 12" fill="none">
                      <path d="M2 8C50 2 150 2 198 8" stroke="#14B8A6" strokeWidth="4" strokeLinecap="round"/>
                    </svg>
                  </span>{' '}
                  com seu dinheiro
                </h2>
                
                <p className="text-lg sm:text-xl text-gray-600 mb-8 sm:mb-10 max-w-xl mx-auto lg:mx-0">
                  Chega de planilhas confusas e apps complicados. O MEUCONTROLE te mostra, de forma simples, como ter 
                  <strong className="text-gray-900"> controle, clareza e tranquilidade</strong> financeira.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-6">
                  <button
                    onClick={() => navigate('/login')}
                    className="group px-8 py-4 bg-teal-600 text-white rounded-2xl font-bold text-lg hover:bg-teal-700 transition-all cursor-pointer whitespace-nowrap shadow-xl shadow-teal-600/30 flex items-center justify-center gap-3"
                  >
                    Experimente sem custo
                    <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                  </button>
                </div>

                <div className="flex items-center gap-2 justify-center lg:justify-start text-gray-500 text-sm">
                  <i className="ri-lock-line"></i>
                  <span>Não precisa de cartão de crédito</span>
                </div>
              </div>

              {/* Right Visual */}
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-teal-100/50 to-emerald-100/50 rounded-3xl transform rotate-3"></div>
                <div className="relative bg-white rounded-3xl shadow-2xl p-4 sm:p-6 transform -rotate-1 hover:rotate-0 transition-transform duration-500">
                  <img
                    src="https://public.readdy.ai/ai/img_res/f549cbcf-0e51-4b67-9d48-5a16662ef87c.png"
                    alt="Dashboard do MEUCONTROLE"
                    className="w-full h-auto rounded-2xl"
                    loading="lazy"
                  />
                  
                  {/* Floating Cards */}
                  <div className="absolute -top-4 -right-4 sm:-right-8 bg-white rounded-xl shadow-lg p-3 sm:p-4 animate-bounce-slow">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-emerald-100 rounded-full">
                        <i className="ri-check-line text-emerald-600 text-lg sm:text-xl"></i>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Conta paga!</p>
                        <p className="text-sm font-bold text-gray-900">-R$ 89,90</p>
                      </div>
                    </div>
                  </div>

                  <div className="absolute -bottom-4 -left-4 sm:-left-8 bg-white rounded-xl shadow-lg p-3 sm:p-4 animate-bounce-slow animation-delay-500">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-teal-100 rounded-full">
                        <i className="ri-money-dollar-circle-line text-teal-600 text-lg sm:text-xl"></i>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Guardado este mês</p>
                        <p className="text-sm font-bold text-emerald-600">+R$ 450,00</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pain Points Section */}
        <section className="py-16 sm:py-24 bg-gradient-to-b from-gray-50 to-white px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-12 sm:mb-16">
              Você se identifica com alguma dessas situações?
            </h3>
            
            <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
              {[
                { icon: 'ri-emotion-sad-line', text: 'Chega no fim do mês e não sabe pra onde foi o dinheiro' },
                { icon: 'ri-file-list-3-line', text: 'Já tentou planilhas, mas desistiu em uma semana' },
                { icon: 'ri-alarm-warning-line', text: 'Vive esquecendo de pagar contas e pagando multa' },
                { icon: 'ri-heart-pulse-line', text: 'Sente ansiedade só de pensar em dinheiro' },
                { icon: 'ri-question-line', text: 'Não entende termos como "reserva de emergência"' },
                { icon: 'ri-moon-line', text: 'Perde o sono preocupado com as contas' }
              ].map((item, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-4 p-4 sm:p-5 bg-white rounded-2xl border border-gray-100 hover:border-teal-200 hover:shadow-lg transition-all cursor-default group"
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-rose-50 rounded-xl group-hover:bg-rose-100 transition-colors shrink-0">
                    <i className={`${item.icon} text-rose-500 text-2xl`}></i>
                  </div>
                  <p className="text-gray-700 text-left text-sm sm:text-base">{item.text}</p>
                </div>
              ))}
            </div>

            <div className="mt-12 sm:mt-16 p-6 sm:p-8 bg-gradient-to-r from-teal-600 to-emerald-600 rounded-3xl text-white">
              <p className="text-xl sm:text-2xl font-bold mb-2">
                Se você disse "sim" para qualquer uma dessas...
              </p>
              <p className="text-teal-100 text-lg">
                O MEUCONTROLE foi feito especialmente para você. 💚
              </p>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12 sm:mb-16">
              <span className="inline-block px-4 py-2 bg-teal-50 text-teal-700 rounded-full text-sm font-medium mb-4">
                Simples de verdade
              </span>
              <h3 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                Tudo que você precisa,{' '}
                <span className="text-teal-600">nada que não precisa</span>
              </h3>
              <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                Sem funcionalidades confusas. Só o essencial para você ter controle do seu dinheiro.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {[
                {
                  icon: 'ri-pie-chart-2-line',
                  color: 'bg-emerald-100 text-emerald-600',
                  title: 'Veja para onde vai seu dinheiro',
                  description: 'Gráficos simples que mostram seus gastos por categoria. Sem números confusos.'
                },
                {
                  icon: 'ri-notification-3-line',
                  color: 'bg-amber-100 text-amber-600',
                  title: 'Nunca mais esqueça uma conta',
                  description: 'Lembretes automáticos antes do vencimento. Adeus multas e juros.'
                },
                {
                  icon: 'ri-target-line',
                  color: 'bg-rose-100 text-rose-600',
                  title: 'Realize seus sonhos',
                  description: 'Crie metas e veja seu progresso. Viagem, carro novo, reserva de emergência.'
                },
                {
                  icon: 'ri-robot-2-line',
                  color: 'bg-teal-100 text-teal-600',
                  title: 'Assistente que te entende',
                  description: 'Pergunte qualquer coisa sobre suas finanças. Respostas simples, sem jargões.'
                },
                {
                  icon: 'ri-shield-check-line',
                  color: 'bg-indigo-100 text-indigo-600',
                  title: 'Seus dados protegidos',
                  description: 'Criptografia de ponta. Ninguém além de você vê suas informações.'
                },
                {
                  icon: 'ri-smartphone-line',
                  color: 'bg-pink-100 text-pink-600',
                  title: 'Acesse de qualquer lugar',
                  description: 'No celular, tablet ou computador. Suas finanças sempre com você.'
                }
              ].map((item, index) => (
                <div 
                  key={index}
                  className="group p-6 sm:p-8 bg-white rounded-3xl border border-gray-100 hover:border-teal-200 hover:shadow-xl transition-all duration-300"
                >
                  <div className={`w-14 h-14 flex items-center justify-center ${item.color} rounded-2xl mb-5 group-hover:scale-110 transition-transform`}>
                    <i className={`${item.icon} text-2xl`}></i>
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h4>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="py-16 sm:py-24 bg-gradient-to-b from-amber-50/50 to-white px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12 sm:mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-full mb-4">
                <i className="ri-star-fill text-amber-500"></i>
                <span className="text-amber-700 text-sm font-medium">Avaliado com 4.9 ★★★★★</span>
              </div>
              <h3 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900">
                Pessoas reais que{' '}
                <span className="text-teal-600">saíram do vermelho</span>
              </h3>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  name: 'Carla Mendes',
                  role: 'Professora',
                  avatar: 'https://public.readdy.ai/ai/img_res/8ab4c6a90c33f075c1c53277095aed94.jpg',
                  text: 'Eu chorava todo mês olhando as contas. Hoje, pela primeira vez em 3 anos, consegui guardar dinheiro. O app me mostrou onde eu estava errando sem me julgar.',
                  highlight: true
                },
                {
                  name: 'Roberto Silva',
                  role: 'Motorista de App',
                  avatar: 'https://readdy.ai/api/search-image?query=brazilian%20man%20casual%20portrait%20friendly%20smile%20natural%20lighting%20headshot%20warm%20expression&width=100&height=100&seq=2&orientation=squarish',
                  text: 'Trabalho o dia todo e não tinha tempo pra planilha. Em 5 minutos por dia eu sei exatamente quanto posso gastar. Simples assim.',
                  highlight: false
                },
                {
                  name: 'Ana Paula Costa',
                  role: 'Mãe de 2 filhos',
                  avatar: 'https://readdy.ai/api/search-image?query=brazilian%20mother%20woman%20portrait%20warm%20smile%20natural%20look%20friendly%20headshot%20casual&width=100&height=100&seq=3&orientation=squarish',
                  text: 'Finalmente consigo planejar as férias das crianças sem aquele aperto no peito. A sensação de controle é libertadora.',
                  highlight: false
                },
                {
                  name: 'Marcos Oliveira',
                  role: 'Autônomo',
                  avatar: 'https://readdy.ai/api/search-image?query=brazilian%20man%20entrepreneur%20portrait%20confident%20smile%20professional%20casual%20headshot&width=100&height=100&seq=4&orientation=squarish',
                  text: 'Saí de R$15 mil em dívidas para zero em 8 meses. O app me deu um plano claro e eu só segui.',
                  highlight: true
                },
                {
                  name: 'Juliana Ferreira',
                  role: 'Estudante',
                  avatar: 'https://readdy.ai/api/search-image?query=young%20brazilian%20woman%20student%20portrait%20friendly%20smile%20natural%20casual%20headshot&width=100&height=100&seq=5&orientation=squarish',
                  text: 'Nunca entendi nada de finanças. Agora sei até o que é reserva de emergência!',
                  highlight: false
                }
              ].map((testimonial, index) => (
                <div 
                  key={index}
                  className={`p-6 sm:p-8 rounded-3xl transition-all duration-300 hover:shadow-xl ${
                    testimonial.highlight 
                      ? 'bg-gradient-to-br from-teal-600 to-emerald-600 text-white' 
                      : 'bg-white border border-gray-100'
                  }`}
                >
                  <div className="flex items-center gap-4 mb-5">
                    <img
                      src={testimonial.avatar}
                      alt={testimonial.name}
                      className="w-14 h-14 rounded-full object-cover"
                      loading="lazy"
                    />
                    <div>
                      <p className={`font-bold ${testimonial.highlight ? 'text-white' : 'text-gray-900'}`}>
                        {testimonial.name}
                      </p>
                      <p className={`text-sm ${testimonial.highlight ? 'text-teal-100' : 'text-gray-500'}`}>
                        {testimonial.role}
                      </p>
                    </div>
                  </div>
                  <div className={`mb-4 ${testimonial.highlight ? 'text-teal-200' : 'text-teal-300'}`}>
                    <i className="ri-double-quotes-l text-4xl"></i>
                  </div>
                  <p className={`text-base sm:text-lg leading-relaxed ${testimonial.highlight ? 'text-white' : 'text-gray-700'}`}>
                    {testimonial.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How it Works */}
        <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12 sm:mb-16">
              <h3 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                Comece em <span className="text-teal-600">3 passos simples</span>
              </h3>
              <p className="text-gray-600 text-lg">
                Sem complicação. Sem burocracia. Sem dor de cabeça.
              </p>
            </div>

            <div className="grid sm:grid-cols-3 gap-8 sm:gap-12">
              {[
                {
                  step: '1',
                  icon: 'ri-user-add-line',
                  title: 'Crie sua conta grátis',
                  description: 'Leva menos de 1 minuto. Só precisa do seu e-mail.'
                },
                {
                  step: '2',
                  icon: 'ri-add-circle-line',
                  title: 'Adicione suas contas',
                  description: 'Registre seus gastos e receitas de forma simples.'
                },
                {
                  step: '3',
                  icon: 'ri-line-chart-line',
                  title: 'Veja a mágica acontecer',
                  description: 'O app organiza tudo e te mostra onde economizar.'
                }
              ].map((item, index) => (
                <div key={index} className="text-center relative">
                  {index < 2 && (
                    <div className="hidden sm:block absolute top-12 left-[60%] w-[80%] border-t-2 border-dashed border-teal-200"></div>
                  )}
                  <div className="relative inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-3xl mb-6 shadow-xl shadow-teal-500/30">
                    <i className={`${item.icon} text-white text-4xl`}></i>
                    <span className="absolute -top-2 -right-2 w-8 h-8 bg-amber-400 rounded-full flex items-center justify-center text-white font-bold text-sm">
                      {item.step}
                    </span>
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h4>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Trust Section */}
        <section className="py-16 sm:py-24 bg-gradient-to-br from-slate-800 to-slate-900 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <span className="inline-block px-4 py-2 bg-teal-500/20 text-teal-300 rounded-full text-sm font-medium mb-6">
                  Sua segurança em primeiro lugar
                </span>
                <h3 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
                  Seus dados estão{' '}
                  <span className="text-teal-400">100% protegidos</span>
                </h3>
                <p className="text-slate-300 text-lg mb-8">
                  Usamos a mesma tecnologia de segurança dos maiores bancos do mundo. 
                  Seus dados são criptografados e nunca são compartilhados com terceiros.
                </p>
                
                <div className="grid sm:grid-cols-2 gap-4">
                  {[
                    { icon: 'ri-shield-check-line', text: 'Criptografia de ponta' },
                    { icon: 'ri-lock-password-line', text: 'Dados protegidos' },
                    { icon: 'ri-eye-off-line', text: 'Privacidade total' },
                    { icon: 'ri-verified-badge-line', text: 'Certificado SSL' }
                  ].map((item, index) => (
                    <div key={index} className="flex items-center gap-3 text-slate-200">
                      <div className="w-10 h-10 flex items-center justify-center bg-teal-500/20 rounded-lg">
                        <i className={`${item.icon} text-teal-400 text-xl`}></i>
                      </div>
                      <span>{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="relative">
                <div className="bg-gradient-to-br from-teal-500/10 to-emerald-500/10 rounded-3xl p-8 sm:p-12 border border-teal-500/20">
                  <div className="text-center">
                    <div className="text-7xl sm:text-8xl font-bold text-white mb-4">7</div>
                    <div className="text-2xl sm:text-3xl font-bold text-teal-400 mb-4">dias grátis</div>
                    <p className="text-slate-300 mb-8">
                      Teste todas as funcionalidades sem compromisso. 
                      Cancele quando quiser.
                    </p>
                    <button
                      onClick={() => navigate('/login')}
                      className="w-full sm:w-auto px-8 py-4 bg-white text-slate-900 rounded-2xl font-bold text-lg hover:bg-teal-50 transition-all cursor-pointer whitespace-nowrap"
                    >
                      Começar meu teste grátis
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-gray-50">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12 sm:mb-16">
              <span className="inline-block px-4 py-2 bg-teal-50 text-teal-700 rounded-full text-sm font-medium mb-4">
                Tire suas dúvidas
              </span>
              <h3 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                Perguntas <span className="text-teal-600">Frequentes</span>
              </h3>
              <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                Tudo que você precisa saber sobre o MEUCONTROLE antes de começar.
              </p>
            </div>

            <div className="space-y-4">
              {[
                {
                  question: "O MEUCONTROLE é gratuito?",
                  answer: "Sim! Você pode testar o MEUCONTROLE gratuitamente por 7 dias, sem precisar de cartão de crédito. Após o período de teste, você pode escolher um dos nossos planos ou continuar com funcionalidades básicas gratuitas."
                },
                {
                  question: "Preciso entender de finanças para usar o app?",
                  answer: "Não! O MEUCONTROLE foi criado especialmente para pessoas que não entendem de finanças. Usamos linguagem simples, sem jargões técnicos, e nosso assistente de IA está sempre disponível para tirar suas dúvidas de forma clara e acessível."
                },
                {
                  question: "Meus dados financeiros estão seguros?",
                  answer: "Absolutamente! Usamos criptografia de ponta a ponta, a mesma tecnologia dos maiores bancos do mundo. Seus dados são protegidos e nunca são compartilhados com terceiros. Temos certificado SSL e seguimos todas as normas de segurança digital."
                },
                {
                  question: "Posso usar o MEUCONTROLE no celular?",
                  answer: "Sim! O MEUCONTROLE funciona perfeitamente em qualquer dispositivo: celular, tablet ou computador. Basta acessar pelo navegador e suas finanças estarão sempre com você, sincronizadas em tempo real."
                },
                {
                  question: "Como o app me ajuda a sair das dívidas?",
                  answer: "O MEUCONTROLE oferece um módulo completo de gestão de dívidas. Você cadastra suas dívidas, o app calcula juros, cria um plano de pagamento personalizado e envia lembretes para você nunca esquecer uma parcela. Muitos usuários saíram de dívidas em menos de 12 meses usando nosso sistema."
                },
                {
                  question: "O que é o assistente de IA do MEUCONTROLE?",
                  answer: "É um assistente virtual inteligente que responde suas dúvidas sobre finanças de forma simples e personalizada. Você pode perguntar coisas como 'quanto posso gastar este mês?' ou 'como economizar para uma viagem?' e receber respostas baseadas nos seus dados reais."
                }
              ].map((faq, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:border-teal-200 transition-all duration-300 hover:shadow-lg"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-5 sm:p-6 text-left cursor-pointer"
                  >
                    <h4 className="text-lg font-semibold text-gray-900 pr-4">
                      <a href={`#faq-${index}`} className="hover:text-teal-600 transition-colors">
                        {faq.question}
                      </a>
                    </h4>
                    <div className={`w-10 h-10 flex items-center justify-center bg-teal-50 rounded-full shrink-0 transition-transform duration-300 ${openFaq === index ? 'rotate-180' : ''}`}>
                      <i className={`ri-arrow-down-s-line text-teal-600 text-xl`}></i>
                    </div>
                  </button>
                  <div 
                    className={`overflow-hidden transition-all duration-300 ${
                      openFaq === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="px-5 sm:px-6 pb-5 sm:pb-6">
                      <p className="text-gray-600 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-12 text-center">
              <p className="text-gray-600 mb-4">Ainda tem dúvidas?</p>
              <button
                onClick={() => {
                  const widget = document.querySelector('#vapi-widget-floating-button') as HTMLElement;
                  if (widget) widget.click();
                }}
                className="inline-flex items-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-full font-semibold hover:bg-teal-700 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-chat-3-line"></i>
                Fale com nosso suporte
              </button>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section ref={ctaSectionRef} className="relative py-24 sm:py-32 px-4 sm:px-6 lg:px-8 overflow-hidden">
          {videoLoaded ? (
            <video
              ref={videoRef}
              className="absolute inset-0 w-full h-full object-cover"
              src="https://public.readdy.ai/ai/video_res/a9e13e71-8cd3-46ae-ae23-892bbd90c9b9.mp4"
              autoPlay
              muted
              loop
              playsInline
            />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-teal-800 to-slate-900" />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
          
          <div className="relative max-w-4xl mx-auto text-center">
            <h3 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              COMECE HOJE.<br />
              <span className="text-teal-300">MUDE SUA VIDA.</span>
            </h3>
            <p className="text-xl sm:text-2xl text-white/90 mb-10 max-w-2xl mx-auto">
              Milhares de pessoas já transformaram sua relação com o dinheiro. 
              Você pode ser a próxima.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="group inline-flex items-center gap-4 px-8 sm:px-10 py-5 bg-teal-500 text-white rounded-full font-bold text-lg sm:text-xl hover:bg-teal-400 transition-all cursor-pointer whitespace-nowrap shadow-2xl shadow-teal-500/40"
            >
              <span>Experimente sem custo</span>
              <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full group-hover:bg-white/30 transition-colors">
                <i className="ri-arrow-right-line text-xl"></i>
              </div>
            </button>
            <p className="mt-6 text-white/70 text-sm">
              Sem cartão de crédito • Cancele quando quiser
            </p>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-16 sm:py-20 bg-slate-50 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
              {/* Left Column */}
              <div>
                <h2 
                  className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4"
                  style={{ fontFamily: '"Pacifico", serif' }}
                >
                  MEUCONTROLE
                </h2>
                <p className="text-gray-600 mb-8 max-w-md">
                  O app de finanças pessoais mais simples do Brasil. 
                  Feito para pessoas reais, com vidas reais.
                </p>

                {/* Newsletter Form */}
                <div className="mb-8">
                  <p className="text-sm font-semibold text-gray-700 mb-3">
                    Receba dicas gratuitas de finanças
                  </p>
                  {submitted ? (
                    <div className="flex items-center gap-2 text-emerald-600 bg-emerald-50 px-4 py-3 rounded-xl">
                      <i className="ri-check-line text-xl"></i>
                      <span>Obrigado! Você receberá nossas dicas em breve.</span>
                    </div>
                  ) : (
                    <form 
                      id="newsletter-landing-form"
                      data-readdy-form
                      onSubmit={handleNewsletterSubmit}
                      className="flex gap-3"
                    >
                      <input
                        type="email"
                        name="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="seu@email.com"
                        className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none text-sm"
                        required
                      />
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="px-6 py-3 bg-gray-900 text-white rounded-full font-semibold hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                      >
                        {isSubmitting ? 'Enviando...' : 'Enviar'}
                      </button>
                    </form>
                  )}
                </div>

                {/* Social Links */}
                <div className="flex gap-4">
                  {['instagram', 'facebook', 'twitter', 'youtube'].map((social) => (
                    <a
                      key={social}
                      href="#"
                      className="w-10 h-10 flex items-center justify-center bg-gray-200 rounded-full hover:bg-teal-500 hover:text-white transition-colors cursor-pointer"
                    >
                      <i className={`ri-${social}-line text-xl`}></i>
                    </a>
                  ))}
                </div>
              </div>

              {/* Right Column - Links */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-8">
                <div>
                  <h4 className="font-bold text-gray-900 mb-4">Produto</h4>
                  <ul className="space-y-3">
                    {['Funcionalidades', 'Preços', 'Segurança', 'Atualizações'].map((link) => (
                      <li key={link}>
                        <a href="#" className="text-gray-600 hover:text-teal-600 transition-colors cursor-pointer">
                          {link}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-4">Empresa</h4>
                  <ul className="space-y-3">
                    {['Sobre nós', 'Blog', 'Carreiras', 'Contato'].map((link) => (
                      <li key={link}>
                        <a href="#" className="text-gray-600 hover:text-teal-600 transition-colors cursor-pointer">
                          {link}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-4">Suporte</h4>
                  <ul className="space-y-3">
                    {['Central de Ajuda', 'Comunidade', 'Tutoriais', 'Status'].map((link) => (
                      <li key={link}>
                        <a href="#" className="text-gray-600 hover:text-teal-600 transition-colors cursor-pointer">
                          {link}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Bottom Bar */}
            <div className="mt-12 pt-8 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-gray-500 text-sm">
                © 2025 MEUCONTROLE. Todos os direitos reservados.
              </p>
              <div className="flex items-center gap-6 text-sm">
                <a href="#" className="text-gray-500 hover:text-teal-600 transition-colors cursor-pointer">
                  Termos de Uso
                </a>
                <a href="#" className="text-gray-500 hover:text-teal-600 transition-colors cursor-pointer">
                  Privacidade
                </a>
                <a 
                  href="https://readdy.ai/?ref=logo" 
                  target="_blank" 
                  rel="nofollow noopener"
                  className="text-gray-500 hover:text-teal-600 transition-colors cursor-pointer"
                >
                  Powered by Readdy
                </a>
              </div>
            </div>
          </div>
        </footer>
      </div>

      <style>{`
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .animate-bounce-slow {
          animation: bounce-slow 3s ease-in-out infinite;
        }
        .animation-delay-500 {
          animation-delay: 0.5s;
        }
      `}</style>
    </>
  );
}
